import axios from 'axios';
import { API_URL, API_ENDPOINTS, normalizeApiUrl } from '../constants/api';
import { getSessionExpiredMessage } from '../branding/runtimeCopy';
import {
  clearAuthState,
  deleteAuthToken,
  deleteOrgId,
  deleteTenantId,
  deleteTenantSlug,
  deleteUserId,
  deleteUserRole,
  getAuthToken,
  getTenantSlug
} from '../utils/auth';
import { toast } from 'react-toastify';

/** Config from brand manifest applied to every request (headers + tenant slug hint). */
export type ApiBrandConfig = {
  headers?: Record<string, string>;
  tenantSlug?: string;
  tenantId?: string;
} | null;

const api = axios.create({
  baseURL: API_URL,
  timeout: 10000,
  withCredentials: true
});

let brandApiConfig: ApiBrandConfig = null;

export function setApiBaseUrl(baseUrl?: string): void {
  if (!baseUrl) {
    return;
  }

  const normalizedUrl = normalizeApiUrl(baseUrl);
  if (!normalizedUrl) {
    return;
  }

  api.defaults.baseURL = normalizedUrl;
}

/** Set brand-derived API config (headers, tenant id). Called by BrandProvider when manifest loads. */
export function setApiBrandConfig(config: ApiBrandConfig): void {
  brandApiConfig = config ?? null;
}

const TENANT_ID_HEADER = 'X-Tenant-Id';

// Public auth endpoints must never carry a stale token/tenant from a previous
// session: doing so makes the backend resolve the OLD tenant and reject a fresh
// login/signup as a cross-tenant request ("Unauthorized tenant context").
const NO_AUTH_PATHS = [
  API_ENDPOINTS.SIGNIN,
  API_ENDPOINTS.SIGNUP,
  API_ENDPOINTS.FORGOT_PASSWORD,
  API_ENDPOINTS.RESET_PASSWORD,
  API_ENDPOINTS.AZURE_CONFIG,
  API_ENDPOINTS.AZURE_START,
  API_ENDPOINTS.AZURE_LOGIN
];

// The Azure SSO endpoints are public too, but the login still needs the BRAND
// tenant hint (a branded hostname's workspace); never a token-derived slug.
const BRAND_TENANT_HINT_PATHS = [
  API_ENDPOINTS.AZURE_CONFIG,
  API_ENDPOINTS.AZURE_START,
  API_ENDPOINTS.AZURE_LOGIN
];

function matchesPath(paths: string[], url?: string): boolean {
  if (!url) {
    return false;
  }
  return paths.some((path) => url.includes(path));
}

function isNoAuthPath(url?: string): boolean {
  return matchesPath(NO_AUTH_PATHS, url);
}

function normalizeTenantSlugHint(value?: string | null): string | undefined {
  const normalized = typeof value === 'string' ? value.trim() : '';
  return normalized || undefined;
}

function resolveTenantHeaderValue(token?: string | null): string | undefined {
  const brandTenantSlug = normalizeTenantSlugHint(
    brandApiConfig?.tenantSlug ?? brandApiConfig?.tenantId
  );
  if (brandTenantSlug) {
    return brandTenantSlug;
  }
  if (!token) {
    return undefined;
  }
  return normalizeTenantSlugHint(getTenantSlug());
}

api.interceptors.request.use(
  (config) => {
    const noAuth = isNoAuthPath(config.url);
    const token = noAuth ? null : getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (brandApiConfig) {
      if (brandApiConfig.headers) {
        Object.entries(brandApiConfig.headers).forEach(([name, value]) => {
          config.headers[name] = value;
        });
      }
    }
    let tenantHeaderValue: string | undefined;
    if (!noAuth) {
      tenantHeaderValue = resolveTenantHeaderValue(token);
    } else if (matchesPath(BRAND_TENANT_HINT_PATHS, config.url)) {
      // With no token this returns only the brand slug.
      tenantHeaderValue = resolveTenantHeaderValue(null);
    }
    if (tenantHeaderValue) {
      config.headers[TENANT_ID_HEADER] = tenantHeaderValue;
    }
    // Only set default Content-Type if not already specified in the request
    if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // A 401 from a public auth endpoint is a refused sign-in, not an expired
    // session: leave it to the caller to show its own message.
    if (error.response?.status === 401 && !isNoAuthPath(error.config?.url)) {
      // IMMEDIATELY clear all auth tokens to prevent stale tokens
      // from being used by in-flight requests during the redirect window
      clearAuthState();

      // Dispatch custom event so React components with queryClient access
      // can listen and clear the React Query cache
      window.dispatchEvent(new Event('auth:expired'));

      // Show the toast after clearing auth state
      toast.error(getSessionExpiredMessage(), {
        autoClose: 3000,
        closeOnClick: false,
        draggable: false,
        closeButton: false
      });

      // Wait for the toast to be shown before redirecting
      setTimeout(() => {
        window.location.href = '/signin';
      }, 5000);

      return Promise.reject(new Error('Token expired'));
    }
    return Promise.reject(error);
  }
);

export default api;
